import type { CollectInputField } from "@common/schemas/icertis"

const OPTIONS_URL_PLACEHOLDER = /(['[]?)\{\{([^{}]+)\}\}(['\]]?)/g

export type AnswerLabelsFor = (fieldId: string) => string[]

export function shownCollectInputFields(fields: CollectInputField[], answerLabelsFor: AnswerLabelsFor): CollectInputField[] {
  const shownFieldIds = new Set<string>()
  return fields.filter((field) => {
    const condition = field.show_when
    const shown =
      !condition ||
      (shownFieldIds.has(condition.field_id) &&
        answerLabelsFor(condition.field_id).some((label) => condition.values.includes(label)))
    if (shown) shownFieldIds.add(field.id)
    return shown
  })
}

export function optionsUrlFieldIds(optionsUrl: string): string[] {
  return Array.from(optionsUrl.matchAll(OPTIONS_URL_PLACEHOLDER), (match) => match[2])
}

function quotedLookupValue(label: string): string {
  return `'${encodeURIComponent(label)}'`
}

export function resolveOptionsUrl(optionsUrl: string, answerLabelsFor: AnswerLabelsFor): string | null {
  if (optionsUrlFieldIds(optionsUrl).some((fieldId) => answerLabelsFor(fieldId).length === 0)) return null
  return optionsUrl.replace(OPTIONS_URL_PLACEHOLDER, (_placeholder, opening: string, fieldId: string, closing: string) => {
    const labels = answerLabelsFor(fieldId)
    if (opening === "'" && closing === "'") return quotedLookupValue(labels[0])
    if (opening === "[" && closing === "]") return `[${labels.map(quotedLookupValue).join(",")}]`
    return `${opening}${encodeURIComponent(labels[0])}${closing}`
  })
}

function revealingFieldIds(field: CollectInputField): string[] {
  const optionsUrl = "options_url" in field ? field.options_url : null
  const filteringFieldIds = optionsUrl ? optionsUrlFieldIds(optionsUrl) : []
  return Array.from(new Set([...(field.show_when ? [field.show_when.field_id] : []), ...filteringFieldIds])).sort()
}

export function startsRevealedSection(fields: CollectInputField[], index: number): boolean {
  if (index === 0) return false
  return revealingFieldIds(fields[index]).join("|") !== revealingFieldIds(fields[index - 1]).join("|")
}

function filteringFieldIds(field: CollectInputField): string[] {
  const optionsUrl = "options_url" in field ? field.options_url : null
  return optionsUrl ? optionsUrlFieldIds(optionsUrl) : []
}

export function staleFieldsAfterChange(
  fields: CollectInputField[],
  changedFieldId: string,
  answerLabelsFor: AnswerLabelsFor,
): CollectInputField[] {
  const shownFieldIds = new Set(shownCollectInputFields(fields, answerLabelsFor).map((field) => field.id))
  const staleFieldIds = new Set(
    fields
      .filter(
        (field) =>
          field.id !== changedFieldId &&
          (!shownFieldIds.has(field.id) || filteringFieldIds(field).includes(changedFieldId)),
      )
      .map((field) => field.id),
  )
  let grew = true
  while (grew) {
    grew = false
    for (const field of fields) {
      if (
        field.id !== changedFieldId &&
        !staleFieldIds.has(field.id) &&
        filteringFieldIds(field).some((fieldId) => staleFieldIds.has(fieldId))
      ) {
        staleFieldIds.add(field.id)
        grew = true
      }
    }
  }
  return fields.filter((field) => staleFieldIds.has(field.id))
}
